// src/views/reports/ServiceRevenueReport.js
import React, { useState, useEffect, useMemo } from 'react';
import { ArrowUp, ArrowDown, Download, BarChart2, TrendingUp, Package, Layers } from 'lucide-react';
import { startOfDay, endOfDay, startOfMonth, endOfMonth, subMonths, startOfYear, subDays, format } from 'date-fns';
import { formatCurrency } from '../../utils/formatters';
import { CSVLink } from 'react-csv';
import { neutralizeCsvRows } from '../../utils/csvExport'; // s9csv-4e1b7c28
import { db } from '../../firebase/config';
import { collection, onSnapshot } from 'firebase/firestore';
import { canSeeLaborCost, rowsWithCostRates } from '../../utils/jobCosts'; // s6costing-4d8a2f63
import { useOrgJobCosts } from '../../hooks/useJobCosts'; // s6costing-4d8a2f63

const toDateInput = (d) => d.toISOString().split('T')[0];

// ─── Summary Card ─────────────────────────────────────────────────────────────
const SummaryCard = ({ label, value, sub, color = 'text-gray-800' }) => (
    <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
        <p className={`text-2xl font-bold ${color}`}>{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
);

// ─── Sort helper ──────────────────────────────────────────────────────────────
const useSortConfig = (defaultKey = 'revenue') => {
    const [sortConfig, setSortConfig] = useState({ key: defaultKey, direction: 'descending' });
    const requestSort = (key) => setSortConfig(prev =>
        prev.key === key
            ? { key, direction: prev.direction === 'ascending' ? 'descending' : 'ascending' }
            : { key, direction: 'descending' }
    );
    const indicator = (key) => {
        if (sortConfig.key !== key) return <span className="inline-block w-4" />;
        return sortConfig.direction === 'ascending'
            ? <ArrowUp className="inline-block ml-1 h-3.5 w-3.5" />
            : <ArrowDown className="inline-block ml-1 h-3.5 w-3.5" />;
    };
    const sort = (arr, getVal) => [...arr].sort((a, b) => {
        const av = getVal(a), bv = getVal(b);
        if (av < bv) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (av > bv) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
    });
    return { sortConfig, requestSort, indicator, sort };
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ServiceRevenueReport = ({ currentUserProfile }) => {
    const [activeTab, setActiveTab]         = useState('services'); // 'services' | 'templates'
    const [jobs, setJobs]                   = useState([]);
    const [templates, setTemplates]         = useState([]);
    const [isLoading, setIsLoading]         = useState(true);
    const [includeInProgress, setIncludeInProgress] = useState(false);

    const [dateRangeOption, setDateRangeOption] = useState('Last 12 Months');
    const [customStartDate, setCustomStartDate] = useState(toDateInput(subMonths(new Date(), 3)));
    const [customEndDate, setCustomEndDate]     = useState(toDateInput(new Date()));

    const svcSort  = useSortConfig('revenue');
    const tmplSort = useSortConfig('revenue');

    // ── Data fetching ──────────────────────────────────────────────────────────
    useEffect(() => {
        if (!currentUserProfile?.organizationId) { setIsLoading(false); return; }
        const orgId = currentUserProfile.organizationId;
        const loaded = { jobs: false, templates: false };
        const check = () => { if (loaded.jobs && loaded.templates) setIsLoading(false); };

        const unsubJobs = onSnapshot(collection(db, `organizations/${orgId}/jobs`), (snap) => {
            setJobs(snap.docs.map(d => ({ ...d.data(), id: d.id }))); // s6costing-docid: doc id wins over a stale stored `id` field
            loaded.jobs = true; check();
        }, () => { loaded.jobs = true; check(); });

        const unsubTmpl = onSnapshot(collection(db, `organizations/${orgId}/jobTemplates`), (snap) => {
            setTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            loaded.templates = true; check();
        }, () => { loaded.templates = true; check(); });

        return () => { unsubJobs(); unsubTmpl(); };
    }, [currentUserProfile]);

    // ── Date range ─────────────────────────────────────────────────────────────
    const dateRange = useMemo(() => {
        const now = new Date();
        switch (dateRangeOption) {
            case 'All Time':       return { start: null, end: null };
            case 'This Month':     return { start: startOfMonth(now), end: endOfMonth(now) };
            case 'Last Month':     { const s = startOfMonth(subMonths(now, 1)); return { start: s, end: endOfMonth(s) }; }
            case 'Last 3 Months':  return { start: startOfDay(subMonths(now, 3)), end: endOfDay(now) };
            case 'Last 6 Months':  return { start: startOfDay(subMonths(now, 6)), end: endOfDay(now) };
            case 'Last 12 Months': return { start: startOfDay(subMonths(now, 12)), end: endOfDay(now) };
            case 'YTD':            return { start: startOfYear(now), end: endOfDay(now) };
            case 'Custom': {
                const s = new Date(`${customStartDate}T00:00:00`);
                const e = new Date(`${customEndDate}T23:59:59`);
                return { start: s, end: e };
            }
            default: return { start: null, end: null };
        }
    }, [dateRangeOption, customStartDate, customEndDate]);

    // ── Filter jobs ────────────────────────────────────────────────────────────
    const filteredJobs = useMemo(() => {
        const statuses = includeInProgress ? ['Completed', 'In Progress'] : ['Completed'];
        return jobs.filter(job => {
            if (!statuses.includes(job.status)) return false;
            if (dateRange.start && dateRange.end) {
                const d = job.completedAt?.toDate?.() || job.startAt?.toDate?.() || null;
                if (!d || d < dateRange.start || d > dateRange.end) return false;
            }
            return true;
        });
    }, [jobs, dateRange, includeInProgress]);

    // ── By Service aggregation ─────────────────────────────────────────────────
    const serviceRows = useMemo(() => {
        const map = {};
        filteredJobs.forEach(job => {
            (job.lineItems || []).forEach(item => {
                const key   = item.serviceId || item.name || 'Unnamed';
                const name  = item.name || key;
                const qty   = parseFloat(item.quantity) || 1;
                const price = parseFloat(item.price) || 0;
                const rev   = qty * price;
                if (!map[key]) map[key] = { key, name, serviceId: item.serviceId || null, jobIds: new Set(), unitsSold: 0, revenue: 0 };
                map[key].jobIds.add(job.id);
                map[key].unitsSold += qty;
                map[key].revenue   += rev;
            });
        });

        const totalRev = Object.values(map).reduce((s, r) => s + r.revenue, 0);

        const rows = Object.values(map).map(r => ({
            ...r,
            jobCount:   r.jobIds.size,
            avgUnitPrice: r.unitsSold > 0 ? r.revenue / r.unitsSold : 0,
            revenuePct:   totalRev > 0 ? (r.revenue / totalRev) * 100 : 0,
        }));

        const getVal = (r) => {
            const v = r[svcSort.sortConfig.key];
            return typeof v === 'number' ? v : (typeof v === 'string' ? v.toLowerCase() : 0);
        };
        return svcSort.sort(rows, getVal);
    }, [filteredJobs, svcSort.sortConfig]);

    // s6costing-4d8a2f63 — S6b: labour cost rates come from jobCosts (Owner/Admin/Manager only),
    // falling back to a not-yet-migrated row's own rate. Display-only copies; nothing is saved.
    const { ratesByJob } = useOrgJobCosts(currentUserProfile?.organizationId, canSeeLaborCost(currentUserProfile));

    // ── By Template aggregation ────────────────────────────────────────────────
    const templateRows = useMemo(() => {
        const map = {};
        filteredJobs.forEach(job => {
            const key  = job.templateId || '__none__';
            const tmpl = templates.find(t => t.id === key);
            const name = tmpl?.name || job.title || 'No Template';
            const rev  = parseFloat(job.invoiceTotal) || 0;
            const labor = rowsWithCostRates(job.jobCosting?.laborEntries, ratesByJob[job.id]).reduce((s, e) => // s6costing-4d8a2f63
                s + (parseFloat(e.hours) || 0) * (parseFloat(e.rate) || 0), 0);

            if (!map[key]) map[key] = { key, name, templateId: key === '__none__' ? null : key, jobCount: 0, completedCount: 0, revenue: 0, laborCost: 0 };
            map[key].jobCount++;
            if (job.status === 'Completed') map[key].completedCount++;
            map[key].revenue   += rev;
            map[key].laborCost += labor;
        });

        const rows = Object.values(map).map(r => {
            const margin = r.revenue > 0 ? ((r.revenue - r.laborCost) / r.revenue) * 100 : null;
            return {
                ...r,
                avgJobValue: r.jobCount > 0 ? r.revenue / r.jobCount : 0,
                grossMargin: margin,
            };
        });

        const getVal = (r) => {
            const v = r[tmplSort.sortConfig.key];
            return typeof v === 'number' ? v : (v == null ? -Infinity : typeof v === 'string' ? v.toLowerCase() : 0);
        };
        return tmplSort.sort(rows, getVal);
    }, [filteredJobs, templates, tmplSort.sortConfig, ratesByJob]);

    // ── Summary totals ─────────────────────────────────────────────────────────
    const svcSummary = useMemo(() => {
        const totalRev  = serviceRows.reduce((s, r) => s + r.revenue, 0);
        const topRow    = [...serviceRows].sort((a, b) => b.revenue - a.revenue)[0];
        return { totalRev, count: serviceRows.length, topService: topRow?.name || '—', topRev: topRow?.revenue || 0 };
    }, [serviceRows]);

    const tmplSummary = useMemo(() => {
        const totalRev  = templateRows.reduce((s, r) => s + r.revenue, 0);
        const topRow    = [...templateRows].sort((a, b) => b.revenue - a.revenue)[0];
        const avgMargin = templateRows.filter(r => r.grossMargin != null);
        const avgM      = avgMargin.length > 0 ? avgMargin.reduce((s, r) => s + r.grossMargin, 0) / avgMargin.length : null;
        return { totalRev, count: templateRows.length, topTemplate: topRow?.name || '—', avgMargin: avgM };
    }, [templateRows]);

    // ── CSV ────────────────────────────────────────────────────────────────────
    const svcCsvHeaders = [
        { label: 'Service', key: 'name' },
        { label: 'Jobs', key: 'jobCount' },
        { label: 'Units Sold', key: 'unitsSold' },
        { label: 'Revenue', key: 'revenue' },
        { label: 'Avg Unit Price', key: 'avgUnitPrice' },
        { label: '% of Revenue', key: 'revenuePct' },
    ];
    const tmplCsvHeaders = [
        { label: 'Template', key: 'name' },
        { label: 'Jobs', key: 'jobCount' },
        { label: 'Completed', key: 'completedCount' },
        { label: 'Total Revenue', key: 'revenue' },
        { label: 'Avg Job Value', key: 'avgJobValue' },
        { label: 'Labor Cost', key: 'laborCost' },
        { label: 'Gross Margin %', key: 'grossMargin' },
    ];
    const svcCsvData  = serviceRows.map(r => ({ ...r, revenue: r.revenue.toFixed(2), avgUnitPrice: r.avgUnitPrice.toFixed(2), revenuePct: r.revenuePct.toFixed(1), unitsSold: r.unitsSold.toFixed(2) }));
    const tmplCsvData = templateRows.map(r => ({ ...r, revenue: r.revenue.toFixed(2), avgJobValue: r.avgJobValue.toFixed(2), laborCost: r.laborCost.toFixed(2), grossMargin: r.grossMargin != null ? r.grossMargin.toFixed(1) : '' }));

    const DATE_RANGE_OPTIONS = ['All Time', 'This Month', 'Last Month', 'Last 3 Months', 'Last 6 Months', 'Last 12 Months', 'YTD', 'Custom'];

    if (isLoading) return <p className="text-center mt-8 text-gray-500">Loading report data…</p>;

    const isServices  = activeTab === 'services';
    const rows        = isServices ? serviceRows  : templateRows;
    const summary     = isServices ? svcSummary   : tmplSummary;
    const csvHeaders  = isServices ? svcCsvHeaders : tmplCsvHeaders;
    const csvData     = isServices ? svcCsvData    : tmplCsvData;
    const csvFilename = `${isServices ? 'service' : 'template'}-revenue-${new Date().toISOString().split('T')[0]}.csv`;

    return (
        <div className="p-6 md:p-10">
            {/* ── Header ── */}
            <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800">Service &amp; Template Revenue</h1>
                    <p className="text-sm text-gray-500 mt-1">Which services and job types are driving your revenue.</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <CSVLink data={neutralizeCsvRows(csvData)} headers={csvHeaders} filename={csvFilename}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors">
                        <Download size={15} /> Export
                    </CSVLink>
                </div>
            </div>

            {/* ── Filters ── */}
            <div className="flex flex-wrap items-end gap-3 p-4 bg-gray-50 border border-gray-200 rounded-xl mb-6">
                {/* Date range */}
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Date Range</label>
                    <select value={dateRangeOption} onChange={e => setDateRangeOption(e.target.value)}
                        className="p-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none">
                        {DATE_RANGE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                </div>
                {dateRangeOption === 'Custom' && (
                    <>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
                            <input type="date" value={customStartDate} onChange={e => setCustomStartDate(e.target.value)}
                                className="p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
                            <input type="date" value={customEndDate} onChange={e => setCustomEndDate(e.target.value)}
                                className="p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                        </div>
                    </>
                )}
                {/* Include In Progress toggle */}
                <label className="flex items-center gap-2 cursor-pointer ml-2 mt-4">
                    <div onClick={() => setIncludeInProgress(p => !p)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${includeInProgress ? 'bg-blue-600' : 'bg-gray-300'}`}>
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${includeInProgress ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </div>
                    <span className="text-sm text-gray-600">Include In Progress</span>
                </label>
                <p className="text-xs text-gray-400 self-end ml-auto">{filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''} in range</p>
            </div>

            {/* ── Tab toggle ── */}
            <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit mb-6">
                <button onClick={() => setActiveTab('services')}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                        isServices ? 'bg-white shadow-sm text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}>
                    <Package size={15} /> By Service
                </button>
                <button onClick={() => setActiveTab('templates')}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                        !isServices ? 'bg-white shadow-sm text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}>
                    <Layers size={15} /> By Template
                </button>
            </div>

            {/* ── Summary cards ── */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <SummaryCard label="Total Revenue" value={formatCurrency(summary.totalRev)} color="text-green-600" />
                <SummaryCard
                    label={isServices ? 'Services tracked' : 'Templates tracked'}
                    value={summary.count}
                    sub={isServices ? 'distinct services' : 'distinct templates'}
                />
                {isServices ? (
                    <>
                        <SummaryCard label="Top Service" value={summary.topService} sub={formatCurrency(summary.topRev) + ' revenue'} color="text-blue-700" />
                        <SummaryCard label="Avg Revenue / Job" value={filteredJobs.length > 0 ? formatCurrency(summary.totalRev / filteredJobs.length) : '—'} sub="across all jobs" />
                    </>
                ) : (
                    <>
                        <SummaryCard label="Top Template" value={summary.topTemplate} color="text-blue-700" />
                        <SummaryCard
                            label="Avg Gross Margin"
                            value={summary.avgMargin != null ? `${summary.avgMargin.toFixed(1)}%` : '—'}
                            sub={summary.avgMargin == null ? 'Add job costing to see' : 'where costing data exists'}
                            color={summary.avgMargin != null ? (summary.avgMargin >= 30 ? 'text-green-600' : 'text-amber-600') : 'text-gray-500'}
                        />
                    </>
                )}
            </div>

            {/* ── Table ── */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-x-auto">
                <table className="w-full text-sm text-left">
                    <thead className="text-xs uppercase bg-gray-50 border-b border-gray-200">
                        {isServices ? (
                            <tr>
                                <th className="px-5 py-3 font-semibold text-gray-600 cursor-pointer" onClick={() => svcSort.requestSort('name')}>
                                    Service {svcSort.indicator('name')}
                                </th>
                                <th className="px-5 py-3 font-semibold text-gray-600 text-right cursor-pointer" onClick={() => svcSort.requestSort('jobCount')}>
                                    Jobs {svcSort.indicator('jobCount')}
                                </th>
                                <th className="px-5 py-3 font-semibold text-gray-600 text-right cursor-pointer" onClick={() => svcSort.requestSort('unitsSold')}>
                                    Units Sold {svcSort.indicator('unitsSold')}
                                </th>
                                <th className="px-5 py-3 font-semibold text-gray-600 text-right cursor-pointer" onClick={() => svcSort.requestSort('revenue')}>
                                    Revenue {svcSort.indicator('revenue')}
                                </th>
                                <th className="px-5 py-3 font-semibold text-gray-600 text-right cursor-pointer" onClick={() => svcSort.requestSort('avgUnitPrice')}>
                                    Avg Unit Price {svcSort.indicator('avgUnitPrice')}
                                </th>
                                <th className="px-5 py-3 font-semibold text-gray-600 text-right cursor-pointer" onClick={() => svcSort.requestSort('revenuePct')}>
                                    % of Total {svcSort.indicator('revenuePct')}
                                </th>
                            </tr>
                        ) : (
                            <tr>
                                <th className="px-5 py-3 font-semibold text-gray-600 cursor-pointer" onClick={() => tmplSort.requestSort('name')}>
                                    Template {tmplSort.indicator('name')}
                                </th>
                                <th className="px-5 py-3 font-semibold text-gray-600 text-right cursor-pointer" onClick={() => tmplSort.requestSort('jobCount')}>
                                    Jobs {tmplSort.indicator('jobCount')}
                                </th>
                                <th className="px-5 py-3 font-semibold text-gray-600 text-right cursor-pointer" onClick={() => tmplSort.requestSort('completedCount')}>
                                    Completed {tmplSort.indicator('completedCount')}
                                </th>
                                <th className="px-5 py-3 font-semibold text-gray-600 text-right cursor-pointer" onClick={() => tmplSort.requestSort('revenue')}>
                                    Revenue {tmplSort.indicator('revenue')}
                                </th>
                                <th className="px-5 py-3 font-semibold text-gray-600 text-right cursor-pointer" onClick={() => tmplSort.requestSort('avgJobValue')}>
                                    Avg Job Value {tmplSort.indicator('avgJobValue')}
                                </th>
                                <th className="px-5 py-3 font-semibold text-gray-600 text-right cursor-pointer" onClick={() => tmplSort.requestSort('laborCost')}
                                    title="From Job Costing entries">
                                    Labor Cost {tmplSort.indicator('laborCost')}
                                </th>
                                <th className="px-5 py-3 font-semibold text-gray-600 text-right cursor-pointer" onClick={() => tmplSort.requestSort('grossMargin')}
                                    title="(Revenue - Labor Cost) / Revenue. Only shows when job costing data exists.">
                                    Margin {tmplSort.indicator('grossMargin')}
                                </th>
                            </tr>
                        )}
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {isServices ? (
                            serviceRows.length === 0 ? (
                                <tr><td colSpan={6} className="px-5 py-10 text-center text-gray-400">No service data for the selected filters.</td></tr>
                            ) : serviceRows.map(row => (
                                <tr key={row.key} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-5 py-4 font-medium text-gray-900">{row.name}</td>
                                    <td className="px-5 py-4 text-right text-gray-600">{row.jobCount}</td>
                                    <td className="px-5 py-4 text-right text-gray-600">{row.unitsSold % 1 === 0 ? row.unitsSold : row.unitsSold.toFixed(2)}</td>
                                    <td className="px-5 py-4 text-right font-semibold text-gray-900">{formatCurrency(row.revenue)}</td>
                                    <td className="px-5 py-4 text-right text-gray-600">{formatCurrency(row.avgUnitPrice)}</td>
                                    <td className="px-5 py-4 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <div className="w-20 bg-gray-100 rounded-full h-1.5">
                                                <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${Math.min(row.revenuePct, 100)}%` }} />
                                            </div>
                                            <span className="text-gray-600 w-10 text-right">{row.revenuePct.toFixed(1)}%</span>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            templateRows.length === 0 ? (
                                <tr><td colSpan={7} className="px-5 py-10 text-center text-gray-400">No template data for the selected filters.</td></tr>
                            ) : templateRows.map(row => (
                                <tr key={row.key} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-5 py-4 font-medium text-gray-900">
                                        {row.name}
                                        {!row.templateId && <span className="ml-2 text-xs text-gray-400 font-normal italic">no template</span>}
                                    </td>
                                    <td className="px-5 py-4 text-right text-gray-600">{row.jobCount}</td>
                                    <td className="px-5 py-4 text-right text-gray-600">{row.completedCount}</td>
                                    <td className="px-5 py-4 text-right font-semibold text-gray-900">{formatCurrency(row.revenue)}</td>
                                    <td className="px-5 py-4 text-right text-gray-600">{formatCurrency(row.avgJobValue)}</td>
                                    <td className="px-5 py-4 text-right text-gray-600">
                                        {row.laborCost > 0 ? formatCurrency(row.laborCost) : <span className="text-gray-300">—</span>}
                                    </td>
                                    <td className="px-5 py-4 text-right font-semibold">
                                        {row.grossMargin != null ? (
                                            <span className={row.grossMargin >= 30 ? 'text-green-600' : row.grossMargin >= 0 ? 'text-amber-600' : 'text-red-600'}>
                                                {row.grossMargin.toFixed(1)}%
                                            </span>
                                        ) : (
                                            <span className="text-gray-300 font-normal text-xs">No costing data</span>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                    {/* Totals row */}
                    {rows.length > 0 && (
                        <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                            {isServices ? (
                                <tr className="font-semibold text-gray-700 text-sm">
                                    <td className="px-5 py-3">Total</td>
                                    <td className="px-5 py-3 text-right">{filteredJobs.length}</td>
                                    <td className="px-5 py-3 text-right">
                                        {serviceRows.reduce((s, r) => s + r.unitsSold, 0).toFixed(0)}
                                    </td>
                                    <td className="px-5 py-3 text-right text-green-700">{formatCurrency(svcSummary.totalRev)}</td>
                                    <td className="px-5 py-3 text-right">—</td>
                                    <td className="px-5 py-3 text-right">100%</td>
                                </tr>
                            ) : (
                                <tr className="font-semibold text-gray-700 text-sm">
                                    <td className="px-5 py-3">Total</td>
                                    <td className="px-5 py-3 text-right">{templateRows.reduce((s, r) => s + r.jobCount, 0)}</td>
                                    <td className="px-5 py-3 text-right">{templateRows.reduce((s, r) => s + r.completedCount, 0)}</td>
                                    <td className="px-5 py-3 text-right text-green-700">{formatCurrency(tmplSummary.totalRev)}</td>
                                    <td className="px-5 py-3 text-right">—</td>
                                    <td className="px-5 py-3 text-right text-gray-600">{formatCurrency(templateRows.reduce((s, r) => s + r.laborCost, 0))}</td>
                                    <td className="px-5 py-3 text-right">
                                        {tmplSummary.avgMargin != null ? `${tmplSummary.avgMargin.toFixed(1)}% avg` : '—'}
                                    </td>
                                </tr>
                            )}
                        </tfoot>
                    )}
                </table>
            </div>
        </div>
    );
};

export default ServiceRevenueReport;